import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FieldAppShell } from '../../components/survey/FieldAppShell'
import { createFacilityContact } from '../../lib/services/facilityContacts'

function Field({
  label,
  type = 'text',
  placeholder,
  value,
  onChange,
}: {
  label: string
  type?: string
  placeholder?: string
  value: string
  onChange: (v: string) => void
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-semibold text-slate-700">{label}</span>
      <input
        type={type}
        placeholder={placeholder}
        className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  )
}

export function ContactNewPage() {
  const { facilityId } = useParams<{ facilityId: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [form, setForm] = useState({ contact_name: '', role_label: '', email: '', phone: '' })
  const [err, setErr] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => createFacilityContact({ ...form, facility_id: facilityId! }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['facility-contacts', facilityId] })
      navigate(`/facilities/${facilityId}`)
    },
    onError: (e: Error) => setErr(e.message),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.contact_name.trim()) { setErr('Name is required.'); return }
    mutation.mutate()
  }

  const set = (key: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [key]: v }))

  return (
    <FieldAppShell eyebrow="Contacts" title="Add Contact" backTo={`/facilities/${facilityId}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {err && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{err}</p>}
        <Field label="Name *"  value={form.contact_name} onChange={set('contact_name')} />
        <Field label="Role"    value={form.role_label}  onChange={set('role_label')} placeholder="e.g. Administrator" />
        <Field label="Email"   value={form.email}       onChange={set('email')}       type="email" />
        <Field label="Phone"   value={form.phone}       onChange={set('phone')}       type="tel" />
        <button
          type="submit"
          disabled={mutation.isPending}
          className="w-full rounded-lg bg-[var(--brand-primary)] py-3 text-sm font-bold text-white disabled:opacity-50"
        >
          {mutation.isPending ? 'Saving...' : 'Add Contact'}
        </button>
      </form>
    </FieldAppShell>
  )
}
